// P5.3 — Active Profile selector (informational mode + future multi-profile).
//
// Pepe's rules (Feb 2026):
//   • If the user has a SINGLE profile, show a passive informational chip
//     (no dropdown — nothing to switch). A small note explains that
//     multi-profile support is coming.
//   • If the user has SEVERAL owned profile_keys (via `profile_key` +
//     `test_profile_keys`), render a dropdown that switches the active
//     profile in component state. The selected profile_key drives which
//     Level 3 block is shown below.
//   • HIDDEN profiles (`brand_sponsor`, `head_coach`) are NEVER offered as
//     a selectable option in the public switcher — even when the user
//     owns the key. We surface a small "private profile" note instead,
//     and the form still works for that profile via the Level 3 block.
//   • This component does NOT mutate the user's primary profile_key on
//     the backend — it is a local UI selector. A future endpoint
//     `PUT /api/users/me/active-profile` will persist the choice.
import React from "react";
import { useLang } from "@/i18n";
import { HIDDEN_PROFILES } from "@/pages/user/profileBlockConfigs";
import { Lock, IdCard } from "lucide-react";

const ActiveProfileSelector = ({ ownedProfiles, activeKey, onChange }) => {
  const { t } = useLang();
  // Split owned profiles into publicly-selectable vs hidden-but-owned.
  const publicOwned = ownedProfiles.filter((k) => !HIDDEN_PROFILES.has(k));
  const hiddenOwned = ownedProfiles.filter((k) => HIDDEN_PROFILES.has(k));
  // Multi-profile UI shows only when more than one PUBLIC profile is owned.
  const isMulti = publicOwned.length > 1;
  const activeIsHidden = HIDDEN_PROFILES.has(activeKey);
  // Phase-1 Profile Data — robust label resolver. When `t()` returns
  // the raw key (i.e. the profile_key isn't registered in
  // `profileBlock.blockTitle.*`, e.g. system roles like `admin` or
  // `staff` showing up because the user has one of those legacy
  // profile_types), fall back to a humanized version of the key so
  // the chip never leaks i18n internals.
  const labelFor = (k) => {
    const resolved = t(`profileBlock.blockTitle.${k}`);
    if (resolved && !resolved.startsWith("profileBlock.")) return resolved;
    if (!k) return "";
    return k.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
      data-testid="active-profile-selector"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
        <IdCard className="w-3.5 h-3.5" />
        {t("profileBlock.selectorLabel")}
      </div>
      {isMulti ? (
        <select
          className="w-full sm:w-auto min-w-[260px] border rounded-md px-3 py-2 bg-white text-sm font-medium text-slate-800"
          value={activeKey}
          onChange={(e) => onChange(e.target.value)}
          data-testid="active-profile-selector-dropdown"
        >
          {publicOwned.map((k) => (
            <option key={k} value={k}>{labelFor(k)}</option>
          ))}
          {/* Hidden-but-owned profiles are listed at the end with a
              lock prefix so the user can still pick them, but only
              after first showing the public ones. They are filtered
              out of the strictly-public switcher when a hidden profile
              isn't in `ownedProfiles`. */}
          {hiddenOwned.map((k) => (
            <option key={k} value={k}>🔒 {labelFor(k)}</option>
          ))}
        </select>
      ) : (
        <div
          className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3.5 py-1.5 text-sm font-medium text-emerald-800"
          data-testid="active-profile-chip"
        >
          {activeIsHidden && <Lock className="w-3.5 h-3.5 text-emerald-700" />}
          {labelFor(activeKey)}
        </div>
      )}
      <p className="text-xs text-slate-500 mt-2">
        {activeIsHidden ? t("profileBlock.selectorHiddenNote") : t("profileBlock.selectorSingleNote")}
      </p>
    </div>
  );
};

export default ActiveProfileSelector;
